"use client"

import { useState, useEffect } from "react"
import "./EmailSystem.css"

const EmailSystem = ({ gameState, addNotification }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [emails, setEmails] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [issueTracker, setIssueTracker] = useState({})
  const [ignoredCount, setIgnoredCount] = useState({})
  const [lastEmailTime, setLastEmailTime] = useState({})
  const [gameStartTime] = useState(Date.now())

  const generateFallbackEmail = (issue, severity, ignoreCount = 0, lawContext = null) => {
    const senders = [
      "Dr. Sarah Chen",
      "Engineer Mike Rodriguez",
      "Farmer Jane Wilson",
      "Security Chief Tom Anderson",
      "Nurse Lisa Park",
      "Scientist Alex Kim",
      "Colony Council",
      "Maintenance Team",
      "Food Services",
      "Medical Staff",
      "Angry Parent",
      "Desperate Worker",
      "Colony Representative",
      "Furious Citizen",
      "Enraged Colonist",
      "Violent Mob Leader",
      "Revolutionary Leader",
      "Execution Committee",
    ]

    const sender = senders[Math.floor(Math.random() * senders.length)]

    const emailTemplates = {
      food: {
        1: {
          subject: "Food Quality Concerns",
          body: `Dear Colony Administrator,

I wanted to bring to your attention some concerns about our food supply. The hydroponic vegetables have been looking a bit wilted lately.

Could we look into improving our farming systems?

Best regards,
${sender}`,
        },
        2: {
          subject: "URGENT: Food Shortage Alert",
          body: `Colony Administrator,

This is getting SERIOUS. We're down to emergency rations and people are starting to panic.

We need immediate action on food production!

${sender}`,
        },
        3: {
          subject: "CRITICAL: PEOPLE ARE STARVING TO DEATH",
          body: `ADMINISTRATOR,

PEOPLE ARE LITERALLY STARVING TO DEATH! My children asked me why there's no food and I don't know what to tell them!

I SAW THREE PEOPLE COLLAPSE FROM HUNGER TODAY!

IF YOU DON'T FIX THIS IMMEDIATELY, WE'RE ORGANIZING A REVOLT!

${sender}`,
        },
        4: {
          subject: "YOU MONSTER - PEOPLE ARE DYING OF HUNGER",
          body: `YOU INCOMPETENT MONSTER,

YOU'VE IGNORED US FOR TOO LONG! PEOPLE ARE DYING OF STARVATION BECAUSE OF YOUR PATHETIC LEADERSHIP!

MY DAUGHTER IS DYING FROM MALNUTRITION! HER BLOOD IS ON YOUR HANDS!

WE'RE COMING FOR YOU, YOU WORTHLESS EXCUSE FOR A LEADER!

${sender}
GRIEVING FATHER`,
        },
        5: {
          subject: "WE'RE GOING TO DESTROY YOU",
          body: `YOU ABSOLUTE MONSTER,

WE'VE HAD ENOUGH OF YOUR INCOMPETENCE! CHILDREN ARE STARVING TO DEATH WHILE YOU DO NOTHING!

WE'RE FORMING AN ARMED MOB TO DRAG YOU OUT OF OFFICE!

YOU'RE A MURDERER AND WE'RE GOING TO MAKE YOU PAY!

${sender}
VIOLENT MOB LEADER`,
        },
        6: {
          subject: "DEATH SENTENCE - WE WILL EXECUTE YOU",
          body: `YOU SICK MONSTER,

THE COLONY HAS VOTED UNANIMOUSLY TO EXECUTE YOU FOR MASS MURDER!

WE'RE GOING TO DRAG YOU TO THE TOWN SQUARE!

YOU WILL ROT AS A WARNING TO FUTURE LEADERS!

PREPARE TO DIE, YOU WORTHLESS SCUM!

${sender}
EXECUTION COMMITTEE`,
        },
      },
      water: {
        1: {
          subject: "Water Taste Issues",
          body: `Hello,

The water has been tasting a bit metallic lately. Probably just the filters need changing.

Thanks,
${sender}`,
        },
        2: {
          subject: "Water Rationing Concerns",
          body: `Administrator,

The water rationing is really affecting our daily lives. Showers are limited and we can't even wash our clothes properly.

Can we increase water production?

${sender}`,
        },
        3: {
          subject: "WE'RE DYING OF THIRST!",
          body: `WHAT IS WRONG WITH YOU?!

There's barely any water left! People are dehydrated and getting sick! My neighbor collapsed yesterday from dehydration!

GET US WATER NOW OR WE'RE ALL GOING TO DIE!

${sender}`,
        },
        4: {
          subject: "YOU MONSTER - NO WATER FOR DAYS",
          body: `YOU WORTHLESS PIECE OF GARBAGE,

WE HAVEN'T HAD CLEAN WATER FOR DAYS! PEOPLE ARE DRINKING THEIR OWN URINE TO SURVIVE!

MY WIFE IS DYING IN THE HOSPITAL BECAUSE OF DEHYDRATION!

FIX THE DARN WATER OR WE'LL REMOVE YOU FROM OFFICE!

${sender}
DESPERATE HUSBAND`,
        },
        5: {
          subject: "DEATH BY DEHYDRATION - WE HATE YOU",
          body: `YOU MURDEROUS MONSTER,

PEOPLE ARE DYING OF THIRST BECAUSE OF YOUR INCOMPETENCE!

WE'RE GOING TO DRAG YOU TO THE DESERT AND LEAVE YOU TO DIE WITHOUT WATER!

SEE HOW YOU LIKE IT, YOU SADISTIC MANIAC!

${sender}
VENGEANCE SEEKER`,
        },
        6: {
          subject: "WE'LL DROWN YOU IN YOUR OWN FILTH",
          body: `YOU SICK MONSTER,

WE'VE PREPARED A SPECIAL DEATH FOR YOU!

WE'RE GOING TO HOLD YOUR HEAD UNDERWATER UNTIL YOU DROWN!

YOU'LL BEG FOR THE WATER YOU DENIED US!

PREPARE TO DIE, YOU WORTHLESS EXCUSE FOR A HUMAN!

${sender}
DROWNING SQUAD`,
        },
      },
      energy: {
        1: {
          subject: "Power Fluctuations",
          body: `Hi there,

Just wanted to let you know the lights have been flickering in Sector 7. Nothing major, but thought you should know.

Thanks,
${sender}`,
        },
        2: {
          subject: "Power Outages Affecting Work",
          body: `Administrator,

The constant power outages are seriously affecting our productivity. I can't run my equipment properly.

Please prioritize the power grid!

${sender}`,
        },
        3: {
          subject: "NO POWER = NO LIFE SUPPORT = DEATH!!!",
          body: `ARE YOU COMPLETELY INSANE?!

The power keeps cutting out and our life support systems are failing!

THE OXYGEN RECYCLERS STOPPED WORKING!
PEOPLE ARE FREEZING TO DEATH!

FIX THE POWER NOW OR WE'RE ALL DEAD!

${sender}`,
        },
        4: {
          subject: "POWER FAILURE KILLED MY FAMILY",
          body: `YOU MURDEROUS MONSTER,

THE POWER FAILURE KILLED MY ENTIRE FAMILY! THE LIFE SUPPORT FAILED AND THEY SUFFOCATED!

THEIR BLOOD IS ON YOUR HANDS, YOU INCOMPETENT PIECE OF TRASH!

I'M GOING TO ELECTROCUTE YOU WITH THE SAME POWER LINES YOU FAILED TO MAINTAIN!

${sender}
GRIEVING WIDOWER`,
        },
        5: {
          subject: "WE'LL BURN YOU ALIVE",
          body: `YOU WORTHLESS MONSTER,

YOU'VE LEFT US IN THE DARK AND COLD, NOW YOU'LL BURN!

WE'LL USE THE LAST OF OUR POWER TO FRY YOU ALIVE!

DAMN YOU AND YOUR FAILED ADMINISTRATION!

${sender}
ARSONIST MOB`,
        },
        6: {
          subject: "ELECTRICAL EXECUTION AWAITS YOU",
          body: `YOU SICK MONSTER,

WE'RE GOING TO STRAP YOU TO THE POWER GRID AND ELECTROCUTE YOU!

WE'LL FRY YOUR BRAIN WHILE YOU'RE STILL CONSCIOUS!

ENJOY YOUR DEATH, YOU WORTHLESS SCUM!

${sender}
ELECTRICAL DEATH SQUAD`,
        },
      },
      oxygen: {
        1: {
          subject: "Air Quality Issues",
          body: `Administrator,

The air feels a bit stale in some sectors. Might want to check the ventilation systems.

Thanks,
${sender}`,
        },
        2: {
          subject: "OXYGEN LEVELS DROPPING",
          body: `Administrator,

Oxygen levels are getting dangerously low! People are having trouble breathing and some are getting dizzy.

We need more oxygen generators NOW!

${sender}`,
        },
        3: {
          subject: "PEOPLE ARE SUFFOCATING TO DEATH!!!",
          body: `ADMINISTRATOR!

PEOPLE ARE LITERALLY SUFFOCATING! The oxygen levels are so low that people are passing out!

I WATCHED SOMEONE DIE FROM LACK OF OXYGEN TODAY!

FIX THIS NOW OR WE ALL DIE!

${sender}`,
        },
        4: {
          subject: "YOU MONSTER - WE CAN'T BREATHE",
          body: `YOU MURDEROUS MONSTER,

WE CAN'T BREATHE! PEOPLE ARE SUFFOCATING TO DEATH BECAUSE OF YOUR NEGLIGENCE!

MY SON DIED GASPING FOR AIR! HE WAS ONLY 8 YEARS OLD!

I'M GOING TO SUFFOCATE YOU THE SAME WAY HE DIED!

${sender}
MURDEROUS PARENT`,
        },
        5: {
          subject: "WE'LL CHOKE YOU TO DEATH",
          body: `YOU PIECE OF GARBAGE,

WE'RE GOING TO WRAP OUR HANDS AROUND YOUR THROAT AND SQUEEZE UNTIL YOU DIE!

YOU'LL KNOW WHAT IT FEELS LIKE TO SUFFOCATE!

DAMN YOU FOR LETTING US DIE!

${sender}
STRANGLER GANG`,
        },
        6: {
          subject: "AIRLOCK EXECUTION PREPARED",
          body: `YOU SICK MONSTER,

WE'VE PREPARED THE AIRLOCK JUST FOR YOU!

WE'LL LOCK YOU INSIDE AND SLOWLY DRAIN THE AIR!

YOU'LL PANIC AND CLAW AT THE WALLS AS YOU SLOWLY DIE!

SEE YOU IN HELL, YOU WORTHLESS SCUM!

${sender}
EXECUTION SQUAD`,
        },
      },
      health: {
        1: {
          subject: "Minor Health Concerns",
          body: `Administrator,

We've noticed a slight increase in minor illnesses around the colony. Nothing serious, but we should keep an eye on it.

Best,
${sender}`,
        },
        2: {
          subject: "Health Crisis Developing",
          body: `Administrator,

The medical bay is overwhelmed! We have more sick people than we can handle.

We need more medical supplies and staff!

${sender}`,
        },
        3: {
          subject: "PEOPLE ARE DYING FROM DISEASE!!!",
          body: `ADMINISTRATOR!

PEOPLE ARE LITERALLY DYING AND WE CAN'T SAVE THEM! The medical facilities are completely inadequate!

I'VE LOST 5 PATIENTS TODAY ALONE!

BUILD MORE HOSPITALS NOW!

${sender}`,
        },
        4: {
          subject: "MEDICAL NEGLIGENCE - YOU'RE A KILLER",
          body: `YOU MURDEROUS MONSTER,

PEOPLE ARE DYING OF PREVENTABLE DISEASES BECAUSE YOU WON'T BUILD PROPER MEDICAL FACILITIES!

MY MOTHER DIED OF INFECTION BECAUSE WE COULDN'T TREAT HER!

YOU'RE A DARN KILLER AND I'M GOING TO MAKE YOU PAY!

${sender}
GRIEVING SON`,
        },
        5: {
          subject: "WE'LL INFECT YOU WITH DISEASE",
          body: `YOU WORTHLESS MONSTER,

WE'RE GOING TO INJECT YOU WITH EVERY DISEASE IN THE COLONY!

YOU'LL ROT FROM THE INSIDE OUT WHILE WE WATCH!

NO MEDICAL TREATMENT FOR YOU!

${sender}
DISEASE SPREADER`,
        },
        6: {
          subject: "BIOLOGICAL WARFARE AGAINST YOU",
          body: `YOU SICK MONSTER,

WE'VE WEAPONIZED THE DISEASES YOU LET SPREAD!

WE'RE GOING TO RELEASE THEM IN YOUR QUARTERS!

YOU'LL DIE THE SAME HORRIBLE DEATH AS OUR FAMILIES!

NO MERCY FOR MURDERERS!

${sender}
MEDICAL REVENGE SQUAD`,
        },
      },
      morale: {
        1: {
          subject: "Morale Concerns",
          body: `Hi Administrator,

People seem a bit down lately. Maybe we could organize some activities to boost spirits?

Thanks,
${sender}`,
        },
        2: {
          subject: "Colony Morale Plummeting",
          body: `Administrator,

Morale is really low. People are depressed and some are talking about giving up.

We need some positive changes around here.

${sender}`,
        },
        3: {
          subject: "COLONY REVOLT IMMINENT - PEOPLE WANT YOU DEAD",
          body: `ADMINISTRATOR,

PEOPLE ARE TALKING ABOUT KILLING YOU! Morale is at rock bottom and everyone hates living here!

THEY'RE FORMING AN ANGRY MOB!
THEY WANT TO OVERTHROW THE GOVERNMENT!

DO SOMETHING NOW OR FACE THE CONSEQUENCES!

${sender}
ANGRY MOB LEADER`,
        },
        4: {
          subject: "REVOLUTION STARTS NOW - WE HATE YOU",
          body: `YOU TYRANNICAL MONSTER,

THE REVOLUTION HAS BEGUN! WE'RE DONE WITH YOUR OPPRESSIVE GARBAGE!

WE'RE GOING TO DRAG YOU FROM YOUR THRONE AND EXECUTE YOU PUBLICLY!

THE PEOPLE DEMAND YOUR BLOOD!

${sender}
REVOLUTION LEADER`,
        },
        5: {
          subject: "DEATH TO THE ADMINISTRATOR",
          body: `YOU FASCIST PIECE OF GARBAGE,

WE'VE DECLARED WAR ON YOUR ADMINISTRATION!

WE'RE GOING TO HUNT YOU DOWN LIKE THE DOG YOU ARE!

YOUR HEAD WILL BE ON A SPIKE FOR ALL TO SEE!

${sender}
REBEL COMMANDER`,
        },
        6: {
          subject: "PUBLIC EXECUTION SCHEDULED",
          body: `YOU DICTATORIAL MONSTER,

WE'VE BUILT A GUILLOTINE IN THE TOWN SQUARE!

WE'RE GOING TO DRAG YOU THERE AND CUT OFF YOUR HEAD!

THE WHOLE COLONY WILL CHEER AS YOUR BLOOD FLOWS!

YOUR REIGN OF TERROR ENDS NOW!

${sender}
EXECUTIONER`,
        },
      },
      law: {
        1: {
          subject: "Concerns About New Law",
          body: `Dear Administrator,

I have some concerns about the new law "${lawContext?.text || "recent legislation"}". While I understand the intent, I worry about the implementation.

Could we discuss this further?

Respectfully,
${sender}`,
        },
        2: {
          subject: "Law Opposition Growing",
          body: `Administrator,

There's growing opposition to "${lawContext?.text || "the new law"}". People are upset and organizing protests.

You might want to reconsider this policy.

${sender}`,
        },
        3: {
          subject: "LAW PROTESTS TURNING VIOLENT",
          body: `ADMINISTRATOR!

The protests against "${lawContext?.text || "your law"}" are turning violent! People are FURIOUS!

THEY'RE CALLING FOR YOUR REMOVAL FROM OFFICE!

REPEAL THIS LAW NOW!

${sender}`,
        },
        4: {
          subject: "YOUR OPPRESSIVE LAW IS DESTROYING US",
          body: `YOU DICTATOR,

YOUR LAW "${lawContext?.text || "this terrible policy"}" IS DESTROYING OUR LIVES!

PEOPLE ARE SUFFERING BECAUSE OF YOUR TYRANNICAL GARBAGE!

WE'RE GOING TO OVERTHROW YOU AND BURN YOUR LAWS!

${sender}
ANGRY CITIZEN`,
        },
        5: {
          subject: "DEATH TO THE LAWMAKER",
          body: `YOU PIECE OF GARBAGE,

YOUR LAW "${lawContext?.text || "this tyranny"}" HAS PUSHED US TOO FAR!

WE'RE GOING TO KILL YOU FOR WHAT YOU'VE DONE TO US!

YOUR LAWS WILL DIE WITH YOU!

${sender}
REVOLUTIONARY LEADER`,
        },
        6: {
          subject: "LAW ENFORCEMENT WILL EXECUTE YOU",
          body: `YOU SICK TYRANT,

WE'VE TURNED YOUR OWN LAW ENFORCEMENT AGAINST YOU!

THEY'RE GOING TO ARREST YOU AND EXECUTE YOU FOR CRIMES AGAINST HUMANITY!

YOUR LAW "${lawContext?.text || "this abomination"}" WILL BE REPEALED OVER YOUR DEAD BODY!

${sender}
JUSTICE SQUAD`,
        },
      },
    }

    // Escalate severity based on ignore count
    const escalatedSeverity = Math.min(6, severity + Math.floor(ignoreCount / 2))

    const template = emailTemplates[issue]?.[escalatedSeverity] ||
      emailTemplates[issue]?.[severity] || {
        subject: "Colony Concern",
        body: `Dear Administrator,

We have concerns about the colony's ${issue} situation.

Please address this soon.

${sender}`,
      }

    return {
      sender,
      subject: template.subject,
      body: template.body,
      timestamp: new Date().toLocaleString(),
      read: false,
      severity: escalatedSeverity,
      originalIssue: issue,
    }
  }

  const checkForIssues = () => {
    if (!gameState || !gameState.resources) return

    const { resources, colonyHealth, colonyMorale, laws } = gameState
    const issues = []
    const currentTime = Date.now()

    // Don't send emails for first 30 seconds
    if (currentTime - gameStartTime < 30000) {
      return
    }

    // Check for critical issues - VERY LOW THRESHOLDS ONLY
    if (resources.food < 50) {
      const severity =
        resources.food < 10 ? 6 : resources.food < 20 ? 5 : resources.food < 30 ? 4 : resources.food < 40 ? 3 : 2
      issues.push({ type: "food", severity })
    }

    if (resources.water < 50) {
      const severity =
        resources.water < 10 ? 6 : resources.water < 20 ? 5 : resources.water < 30 ? 4 : resources.water < 40 ? 3 : 2
      issues.push({ type: "water", severity })
    }

    if (resources.energy < 50) {
      const severity =
        resources.energy < 10
          ? 6
          : resources.energy < 20
            ? 5
            : resources.energy < 30
              ? 4
              : resources.energy < 40
                ? 3
                : 2
      issues.push({ type: "energy", severity })
    }

    if (resources.oxygen < 50) {
      const severity =
        resources.oxygen < 10
          ? 6
          : resources.oxygen < 20
            ? 5
            : resources.oxygen < 30
              ? 4
              : resources.oxygen < 40
                ? 3
                : 2
      issues.push({ type: "oxygen", severity })
    }

    if (colonyHealth < 30) {
      const severity = colonyHealth < 5 ? 6 : colonyHealth < 10 ? 5 : colonyHealth < 15 ? 4 : colonyHealth < 20 ? 3 : 2
      issues.push({ type: "health", severity })
    }

    if (colonyMorale < 30) {
      const severity = colonyMorale < 5 ? 6 : colonyMorale < 10 ? 5 : colonyMorale < 15 ? 4 : colonyMorale < 20 ? 3 : 2
      issues.push({ type: "morale", severity })
    }

    // Check for law-related anger
    if (laws && Array.isArray(laws)) {
      laws.forEach((law) => {
        if (law.active) {
          let severity = 2

          // Water ban laws = EXTREME anger
          if (
            law.text.toLowerCase().includes("water") &&
            (law.text.toLowerCase().includes("not allowed") ||
              law.text.toLowerCase().includes("ban") ||
              law.text.toLowerCase().includes("forbidden") ||
              law.text.toLowerCase().includes("no one"))
          ) {
            severity = 6
          }
          // Food restrictions
          else if (
            law.text.toLowerCase().includes("food") &&
            (law.text.toLowerCase().includes("ban") || law.text.toLowerCase().includes("ration"))
          ) {
            severity = 5
          }
          // Any "ban" or "not allowed" laws
          else if (
            law.text.toLowerCase().includes("ban") ||
            law.text.toLowerCase().includes("not allowed") ||
            law.text.toLowerCase().includes("forbidden")
          ) {
            severity = 4
          }
          // Mandatory laws
          else if (law.text.toLowerCase().includes("mandatory") || law.text.toLowerCase().includes("must")) {
            severity = 3
          }

          issues.push({ type: "law", severity, lawContext: law })
        }
      })
    }

    // Generate emails for issues - MUCH LESS FREQUENT
    for (const issue of issues) {
      const key = issue.lawContext ? `law_${issue.lawContext.id}` : issue.type
      const lastSeverity = issueTracker[key] || 0
      const currentIgnoreCount = ignoredCount[key] || 0
      const lastEmail = lastEmailTime[key] || 0

      const minEmailInterval = 120000 // 2 minutes minimum between emails

      if (currentTime - lastEmail < minEmailInterval) {
        continue
      }

      // Only 15% chance to send email
      if (issue.severity > lastSeverity || (issue.severity >= lastSeverity && Math.random() < 0.15)) {
        // Increment ignore count if issue persists
        if (issue.severity === lastSeverity && lastSeverity > 0) {
          setIgnoredCount((prev) => ({
            ...prev,
            [key]: currentIgnoreCount + 1,
          }))
        }

        const email = generateFallbackEmail(issue.type, issue.severity, currentIgnoreCount, issue.lawContext)

        setEmails((prev) => [
          {
            ...email,
            id: Date.now() + Math.random(),
            type: issue.type,
            severity: email.severity,
          },
          ...prev.slice(0, 29),
        ])

        setUnreadCount((prev) => prev + 1)

        // Update trackers
        setIssueTracker((prev) => ({
          ...prev,
          [key]: issue.severity,
        }))

        setLastEmailTime((prev) => ({
          ...prev,
          [key]: currentTime,
        }))

        // Show notification for urgent emails
        if (email.severity >= 3) {
          addNotification({
            type: email.severity >= 4 ? "error" : "warning",
            title:
              email.severity >= 5
                ? "💀 DEATH THREAT RECEIVED"
                : email.severity >= 4
                  ? "🔥 VIOLENT THREAT"
                  : "📧 Critical Email",
            description:
              email.severity >= 5
                ? `EXECUTION THREAT about ${issue.type}!`
                : email.severity >= 4
                  ? `VIOLENT THREAT about ${issue.type}!`
                  : `Critical complaint about ${issue.type}!`,
          })
        }
      }
    }

    // Clear resolved issues
    Object.keys(issueTracker).forEach((key) => {
      if (!issues.find((i) => (i.lawContext ? `law_${i.lawContext.id}` : i.type) === key)) {
        setIssueTracker((prev) => {
          const newTracker = { ...prev }
          delete newTracker[key]
          return newTracker
        })
        setIgnoredCount((prev) => {
          const newIgnored = { ...prev }
          delete newIgnored[key]
          return newIgnored
        })
        setLastEmailTime((prev) => {
          const newTimes = { ...prev }
          delete newTimes[key]
          return newTimes
        })
      }
    })
  }

  useEffect(() => {
    const interval = setInterval(checkForIssues, 60000) // Check every 60 seconds
    checkForIssues() // Initial check
    return () => clearInterval(interval)
  }, [gameState])

  const markAsRead = (emailId) => {
    setEmails((prev) => prev.map((email) => (email.id === emailId ? { ...email, read: true } : email)))
    setUnreadCount((prev) => Math.max(0, prev - 1))
  }

  const getSeverityColor = (severity) => {
    if (severity >= 6) return "#000000"
    if (severity >= 5) return "#8b0000"
    if (severity >= 4) return "#dc143c"
    if (severity >= 3) return "#f44336"
    if (severity >= 2) return "#ff9800"
    return "#4caf50"
  }

  const getSeverityLabel = (severity) => {
    if (severity >= 6) return "EXECUTION"
    if (severity >= 5) return "DEATH THREAT"
    if (severity >= 4) return "VIOLENT"
    if (severity >= 3) return "CRITICAL"
    if (severity >= 2) return "Urgent"
    return "Mild"
  }

  return (
    <div className={`email-system ${isOpen ? "open" : ""}`}>
      <button className="email-toggle" onClick={() => setIsOpen(!isOpen)} title="Colony Email System">
        <span className="email-icon">📧</span>
        <span className="email-label">MAIL</span>
        {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
      </button>

      {isOpen && (
        <div className="email-panel">
          <div className="email-header">
            <div className="email-title">
              <span className="email-avatar">📧</span>
              <div className="email-info">
                <h3>Colony Mail</h3>
                <p>
                  {emails.length} messages • {unreadCount} unread
                </p>
              </div>
            </div>
            <button className="close-btn" onClick={() => setIsOpen(false)}>
              ×
            </button>
          </div>

          <div className="emails-container">
            {emails.length === 0 ? (
              <div className="no-emails">
                <span className="no-emails-icon">📭</span>
                <p>No emails yet</p>
                <p className="no-emails-subtitle">Keep your colony happy to avoid complaints!</p>
              </div>
            ) : (
              emails.map((email) => (
                <div
                  key={email.id}
                  className={`email-item ${!email.read ? "unread" : ""} ${email.severity >= 4 ? "threat" : ""}`}
                  onClick={() => markAsRead(email.id)}
                >
                  <div className="email-item-header">
                    <div className="email-sender">
                      <span className="sender-avatar">
                        {email.severity >= 6
                          ? "☠️"
                          : email.severity >= 5
                            ? "💀"
                            : email.severity >= 4
                              ? "🔥"
                              : email.severity >= 3
                                ? "😡"
                                : "👤"}
                      </span>
                      <span className="sender-name">{email.sender}</span>
                    </div>
                    <div className="email-meta">
                      <span className="severity-badge" style={{ backgroundColor: getSeverityColor(email.severity) }}>
                        {getSeverityLabel(email.severity)}
                      </span>
                      <span className="email-time">{email.timestamp}</span>
                    </div>
                  </div>
                  <div className="email-subject">{email.subject}</div>
                  <div className="email-body">{email.body}</div>
                  {!email.read && <div className="unread-indicator"></div>}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default EmailSystem
